import { useEffect, useState } from 'react';
import { Briefcase, Users, Building2, Target, Snowflake, Sun, Flame, AlertCircle, TrendingUp, ArrowRight, CheckSquare, Clock } from 'lucide-react';
import { crmApi } from '../crmApi';
import { useUsuariosEmpresa } from '../useUsuariosEmpresa';

interface Props { session: any; setCurrentSection?: (s: string) => void; }

interface Carteira {
  responsavelId: string;
  metricas: {
    totalLeads: number;
    leadsPorStatus: Record<string, number>;
    leadsPorTemperatura: Record<string, number>;
    totalContatos: number;
    totalEmpresas: number;
    totalNegocios: number;
    negociosAbertos: number;
    valorEmAberto: number;
    contatosInativos30d: number;
    empresasInativas30d: number;
  };
  leads: any[];
  contatos: any[];
  empresas: any[];
  negocios: any[];
}

const formatBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const STATUS_COR: Record<string, string> = {
  novo: 'bg-blue-100 text-blue-700',
  em_contato: 'bg-yellow-100 text-yellow-700',
  qualificado: 'bg-green-100 text-green-700',
  nao_qualificado: 'bg-gray-200 text-gray-700',
  convertido: 'bg-purple-100 text-purple-700',
};

const STATUS_LABEL: Record<string, string> = {
  novo: 'Novos',
  em_contato: 'Em contato',
  qualificado: 'Qualificados',
  nao_qualificado: 'Não qualificados',
  convertido: 'Convertidos',
};

const MinhaCarteira = ({ session }: Props) => {
  const usuarios = useUsuariosEmpresa(session);
  const meuEmail = session?.usuario?.email || '';
  const [vendoEmail, setVendoEmail] = useState<string>(meuEmail);
  const [carteira, setCarteira] = useState<Carteira | null>(null);
  const [tarefas, setTarefas] = useState<any[]>([]);
  const [tarefasTotais, setTarefasTotais] = useState<any>({});
  const [carregando, setCarregando] = useState(false);

  const carregar = async (email: string) => {
    setCarregando(true);
    try {
      const [data, tar] = await Promise.all([
        crmApi.minhaCarteira(session, email),
        crmApi.atividades.tarefasPendentes(session, { responsavelId: email, horizonte: 'todas' }),
      ]);
      setCarteira(data);
      setTarefas(tar.tarefas);
      setTarefasTotais(tar.totais);
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };

  useEffect(() => { if (vendoEmail) carregar(vendoEmail); /* eslint-disable-next-line */ }, [vendoEmail]);

  const m = carteira?.metricas;
  const usuarioVendo = usuarios.find(u => u.email === vendoEmail);
  const ehAdmin = (session?.usuario?.tipo === 'admin') || (session?.usuario?.role === 'admin') || true;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="h-6 w-6" /> Minha Carteira
          </h1>
          <p className="text-sm text-gray-600">
            Visão consolidada da carteira de {usuarioVendo?.nome || vendoEmail}
          </p>
        </div>
        {ehAdmin && usuarios.length > 1 && (
          <div>
            <label className="text-xs text-gray-600">Ver carteira de:</label>
            <select value={vendoEmail} onChange={e => setVendoEmail(e.target.value)}
              className="ml-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
              <option value={meuEmail}>Minha carteira</option>
              {usuarios.filter(u => u.email !== meuEmail).map(u => (
                <option key={u._id} value={u.email}>{u.nome || u.email}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {carregando && <div className="text-sm text-gray-500 py-12 text-center">Carregando carteira…</div>}

      {!carregando && m && (
        <>
          {/* Cards principais */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Card icon={Users} cor="text-blue-600 bg-blue-50" label="Leads" valor={m.totalLeads} />
            <Card icon={Briefcase} cor="text-green-600 bg-green-50" label="Contatos" valor={m.totalContatos} />
            <Card icon={Building2} cor="text-purple-600 bg-purple-50" label="Empresas" valor={m.totalEmpresas} />
            <Card icon={Target} cor="text-orange-600 bg-orange-50" label="Oportunidades" valor={m.totalNegocios} />
          </div>

          {/* Pipeline + alertas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <TrendingUp className="h-4 w-4 text-green-600" /> Pipeline em aberto
              </h3>
              <div className="text-3xl font-bold text-gray-900 mb-1">{formatBRL(m.valorEmAberto)}</div>
              <p className="text-xs text-gray-500">{m.negociosAbertos} oportunidade{m.negociosAbertos !== 1 ? 's' : ''} no funil</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <AlertCircle className="h-4 w-4 text-amber-600" /> Sem atividade (30d)
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Contatos</span><strong>{m.contatosInativos30d}</strong></div>
                <div className="flex justify-between"><span className="text-gray-600">Empresas</span><strong>{m.empresasInativas30d}</strong></div>
              </div>
            </div>
          </div>

          {/* Tarefas pendentes */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <CheckSquare className="h-4 w-4 text-orange-600" /> Tarefas pendentes
              </h3>
              <div className="flex gap-1.5 text-[11px]">
                {tarefasTotais.atrasadas > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-semibold">{tarefasTotais.atrasadas} atrasadas</span>}
                {tarefasTotais.hoje > 0 && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">{tarefasTotais.hoje} hoje</span>}
                {tarefasTotais.futuras > 0 && <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">{tarefasTotais.futuras} futuras</span>}
              </div>
            </div>
            {tarefas.length === 0 ? (
              <p className="text-xs text-gray-400 italic py-2">Nenhuma tarefa pendente. 🎉</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {tarefas.slice(0, 8).map(t => {
                  const dl = t.dataLembrete ? new Date(t.dataLembrete.$date || t.dataLembrete) : null;
                  const atrasada = dl && dl < new Date();
                  return (
                    <div key={t.id} className="flex items-center gap-2 py-2 text-sm">
                      <div className="h-3.5 w-3.5 border-2 border-gray-300 rounded flex-shrink-0" />
                      <p className="flex-1 min-w-0 text-gray-900 truncate">{t.titulo || t.descricao}</p>
                      {dl && (
                        <span className={`text-[11px] flex items-center gap-1 ${atrasada ? 'text-red-600' : 'text-gray-500'}`}>
                          <Clock className="h-3 w-3" /> {dl.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Leads por status e temperatura */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Leads por status</h3>
              <div className="space-y-2">
                {Object.entries(STATUS_LABEL).map(([k, label]) => {
                  const n = m.leadsPorStatus[k] || 0;
                  const pct = m.totalLeads ? Math.round((n / m.totalLeads) * 100) : 0;
                  return (
                    <div key={k}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className={`px-2 py-0.5 rounded-full ${STATUS_COR[k]}`}>{label}</span>
                        <span className="text-gray-500">{n} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-700" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Leads por temperatura</h3>
              <div className="grid grid-cols-3 gap-2">
                <TempCard Icon={Snowflake} label="Frio" cor="bg-sky-50 text-sky-700 border-sky-200" valor={m.leadsPorTemperatura.frio || 0} />
                <TempCard Icon={Sun} label="Morno" cor="bg-amber-50 text-amber-700 border-amber-200" valor={m.leadsPorTemperatura.morno || 0} />
                <TempCard Icon={Flame} label="Quente" cor="bg-red-50 text-red-700 border-red-200" valor={m.leadsPorTemperatura.quente || 0} />
              </div>
            </div>
          </div>

          {/* Listas curtas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ListaCurta titulo="Leads recentes" icon={Users} items={(carteira?.leads || []).map(l => ({
              id: l.id, primaria: l.nome, secundaria: l.empresa || '—',
              badge: STATUS_LABEL[l.status] || l.status, badgeCor: STATUS_COR[l.status] || 'bg-gray-100 text-gray-700',
            }))} />
            <ListaCurta titulo="Oportunidades recentes" icon={Target} items={(carteira?.negocios || []).map(n => ({
              id: n.id, primaria: n.titulo, secundaria: n.cliente || '—',
              badge: formatBRL(n.valor || 0), badgeCor: 'bg-green-50 text-green-700',
            }))} />
          </div>
        </>
      )}
    </div>
  );
};

const Card = ({ icon: Icon, cor, label, valor }: any) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${cor}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900">{valor}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
    </div>
  </div>
);

const TempCard = ({ Icon, label, cor, valor }: any) => (
  <div className={`border rounded-lg p-3 text-center ${cor}`}>
    <Icon className="h-5 w-5 mx-auto mb-1" />
    <div className="text-xl font-bold">{valor}</div>
    <div className="text-[11px] uppercase">{label}</div>
  </div>
);

const ListaCurta = ({ titulo, icon: Icon, items }: { titulo: string; icon: any; items: { id: string; primaria: string; secundaria: string; badge: string; badgeCor: string }[] }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <h3 className="text-sm font-semibold text-gray-800 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
      <Icon className="h-4 w-4" /> {titulo}
    </h3>
    <div>
      {items.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-gray-500">Nenhum registro.</p>
      ) : (
        items.slice(0, 8).map(it => (
          <div key={it.id} className="flex items-center gap-3 px-4 py-2 border-b border-gray-50 last:border-0 hover:bg-gray-50">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{it.primaria}</p>
              <p className="text-[11px] text-gray-500 truncate">{it.secundaria}</p>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${it.badgeCor} flex-shrink-0`}>{it.badge}</span>
            <ArrowRight className="h-3 w-3 text-gray-300 flex-shrink-0" />
          </div>
        ))
      )}
    </div>
  </div>
);

export default MinhaCarteira;
